import { useState } from "react";
import { toast } from "react-toastify";
import * as z from "zod";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormItem, FormLabel } from "@/components/ui/form";
import { useUploadAndCreateMasterSpecList } from "@/hooks/useSpecLists";
import { splitExtension } from "@/utils/fileUtils";

const formSchema = z.object({
  name: z.string().optional(),
});
type FormSchema = z.infer<typeof formSchema>;

interface UploadMasterSpecDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function UploadMasterSpecDialog({
  open,
  onOpenChange,
  onSuccess,
}: UploadMasterSpecDialogProps) {
  const [file, setFile] = useState<File | undefined>(undefined);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const { trigger: uploadFile } = useUploadAndCreateMasterSpecList();

  const form = useForm<FormSchema>({
    defaultValues: {
      name: "",
    },
    mode: "all",
    resolver: zodResolver(formSchema),
  });

  const { control, handleSubmit, reset } = form;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFile(event.target.files[0]);
    }
  };

  const resetForm = () => {
    reset();
    setFile(undefined);
    const fileInput = document.getElementById("masterSpecFile") as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const onSubmit: SubmitHandler<FormSchema> = async (data) => {
    try {
      if (!file) return;

      setIsUploading(true);

      const [defaultName, extension] = splitExtension(file.name);
      const baseName = data.name?.trim() || defaultName;
      const finalName = `${baseName}${extension}`;
      const uploadedFile =
        finalName !== file.name ? new File([file], finalName, { type: file.type }) : file;

      await uploadFile(
        {
          file: uploadedFile,
        },
        {
          onSuccess: () => {
            toast.success("Successfully uploaded and processed the file!");
            resetForm();
            onOpenChange(false);
            onSuccess();
          },
          onError: (error) => {
            toast.error(error.message);
          },
        },
      );
    } catch (error) {
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Master Spec</DialogTitle>
          <DialogDescription>Upload a new master spec file.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid gap-4 py-4">
              <FormItem>
                <FormLabel htmlFor="masterSpecFile">Master Spec File (.xls, .xlsx)</FormLabel>
                <Label htmlFor="masterSpecFile" className="hidden">
                  Select file
                </Label>
                <Input
                  accept=".xls,.xlsx"
                  disabled={isUploading}
                  id="masterSpecFile"
                  onChange={handleFileChange}
                  type="file"
                />
              </FormItem>
              <FormItem>
                <FormLabel htmlFor="masterSpecName">Name</FormLabel>
                <FormControl>
                  <Controller
                    control={control}
                    name="name"
                    render={({ field }) => (
                      <Input
                        disabled={isUploading}
                        id="masterSpecName"
                        placeholder={`Optional, defaults to file name`}
                        {...field}
                      />
                    )}
                  />
                </FormControl>
              </FormItem>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isUploading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isUploading || !file}>
                {isUploading ? "Uploading..." : "Upload"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
